/**
 * «El comercio me confirmó que sus clientes aceptaron recibir promociones», sobre la base en uso (fase 4, Alan 17/10:
 * «si tengo el dato es que sí»). Con la declaración, al preparar el envío de una campaña nueva, a quien el archivo no
 * dice si aceptó se lo cuenta como sí y WhatsApp oficial le puede escribir; quien dijo que no, nunca. El quién y el
 * cómo son obligatorios: sin eso, la declaración no prueba nada ante un reclamo. Lo que ya estaba preparado no cambia
 * hasta prepararlo de nuevo (el sí se fija al preparar).
 *
 * Desde el 29/10/2026 (decisión de Alan):
 * - una declaración nueva pide el RESPALDO: la captura o el PDF del mail o del chat donde el comercio lo confirmó;
 * - antes de declarar se ve a cuántos clientes les vuelve «sí» (el criterio del envío) y por qué no a los demás;
 * - si la versión anterior tenía una declaración, se puede decir que sigue valiendo en vez de escribir otra (la base
 *   nueva la hereda, con quien lo confirma y la fecha de hoy). Es lo mismo que pregunta «Listo» al subirla;
 * - declarada, dice si tiene respaldo (y el equipo lo puede ver) y ofrece «Preparar de nuevo» lo ya preparado.
 */

import { useId, useRef, useState } from "react";
import { Check, Loader2, Paperclip, ShieldCheck } from "lucide-react";
import { baseVentasApi } from "../api";
import type { PanoramaConsentimiento, Version } from "../types";
import { cuandoSubida } from "../logic/base";
import { ACEPTA_RESPALDO, leerRespaldo, pesoDelArchivo, problemaDelRespaldo } from "../logic/respaldo";
import { AlcanceDeclaracion, ChipRespaldo, PreparadasDeNuevo } from "./LoQueCambiaLaDeclaracion";
import { usePedido } from "../../emails/hooks/usePedido";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";

export const COMO_MIN = 5;
export const COMO_MAX = 200;

/** `numeroDe`: el número de cada versión por su id (lo arma el historial), para decir de cuál se heredó. */
export function ConsentimientoDeLaBase({ v, alCambiar, numeroDe }: { v: Version; alCambiar: () => void; numeroDe?: (id: string) => number | null }) {
  const idComo = useId();
  const idRespaldo = useId();
  const [abierto, setAbierto] = useState(false);
  const [como, setComo] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errorArchivo, setErrorArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const inputArchivo = useRef<HTMLInputElement>(null);
  const largo = como.trim().replace(/\s+/g, " ").length;
  const valido = largo >= COMO_MIN && largo <= COMO_MAX && !!archivo && !errorArchivo;

  const c = v.consentimiento;
  // lo que cambia declarar: se pide al abrir el formulario y, ya declarada, para lo que falta preparar de nuevo
  const panorama = usePedido<PanoramaConsentimiento>(() => baseVentasApi.panoramaConsentimiento(v.id), `panorama:${v.id}:${c?.at ?? "sin"}`, { activo: abierto || !!c });

  const correr = async (pedido: () => Promise<unknown>) => {
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta<Version>(await pedido());
      if (!l.ok) {
        setError(textoError(l));
        return;
      }
      setAbierto(false);
      setComo("");
      setArchivo(null);
      alCambiar();
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const elegirArchivo = (f: File | null) => {
    setArchivo(f);
    setErrorArchivo(f ? problemaDelRespaldo(f) : "");
  };

  const declarar = async () => {
    if (!valido || guardando || !archivo) return;
    let respaldo;
    try {
      respaldo = await leerRespaldo(archivo);
    } catch (e) {
      setErrorArchivo(e instanceof Error ? e.message : "No pudimos leer el respaldo.");
      return;
    }
    await correr(() => baseVentasApi.declararConsentimiento(v.id, como, respaldo));
  };

  if (c) {
    const deCual = c.heredadaDe ? numeroDe?.(c.heredadaDe) ?? null : null;
    return (
      <div className="w-full rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="consentimiento-declarado">
        <p className="flex items-start gap-2">
          <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            El comercio confirmó que sus clientes aceptaron recibir promociones: «{c.como}».{" "}
            {c.heredadaDe
              ? `Sigue valiendo la de la versión ${deCual ?? "anterior"}: lo confirmó ${c.autor || "alguien del equipo"} el ${cuandoSubida(c.at)}.`
              : `Lo cargó ${c.autor || "alguien del equipo"} el ${cuandoSubida(c.at)}.`}
            <span className="block text-emerald-800">Al preparar un envío, a quien el archivo no dice si aceptó se lo cuenta como sí. Quien dijo que no, nunca.</span>
            {/* el sí se fija al preparar: lo preparado antes de declararlo no cambia solo (Llama Leads, 26/10/2026) */}
            <span className="block text-emerald-800" data-testid="consentimiento-preparar-de-nuevo">
              Las campañas que ya preparaste antes de declararlo no lo toman solas: preparalas de nuevo, en sus Mensajes o en la Audiencia del WhatsApp oficial («Preparar de nuevo»).
            </span>
          </span>
        </p>
        <div className="mt-2">
          <ChipRespaldo c={c} versionId={v.id} />
        </div>
        {panorama.dato && panorama.dato.preparadas.length > 0 && (
          <div className="mt-2">
            <PreparadasDeNuevo preparadas={panorama.dato.preparadas} />
          </div>
        )}
        {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
        <button
          type="button"
          disabled={guardando}
          onClick={() => void correr(() => baseVentasApi.retirarConsentimiento(v.id))}
          className="mt-2 min-h-[44px] rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
        >
          {guardando ? "Retirando…" : "Retirar la declaración"}
        </button>
      </div>
    );
  }

  if (!abierto) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
        >
          <ShieldCheck size={13} aria-hidden="true" /> Declarar el consentimiento
        </button>
      </div>
    );
  }

  const anterior = panorama.dato?.anterior ?? null;
  return (
    <form
      className="w-full space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        void declarar();
      }}
    >
      <p className="text-sm text-slate-700">
        Si el comercio te confirmó que sus clientes aceptaron recibir promociones, a quien el archivo no dice si aceptó le vamos a escribir por WhatsApp
        oficial como si hubiera dicho que sí. Quien dijo que no, nunca.
      </p>
      {panorama.cargando && !panorama.dato ? (
        <p className="text-xs text-slate-500 inline-flex items-center gap-1.5" aria-busy="true">
          <Loader2 size={13} className="animate-spin" aria-hidden="true" /> Contando a cuántos les cambia…
        </p>
      ) : panorama.dato ? (
        <AlcanceDeclaracion alcance={panorama.dato.alcance} />
      ) : null}
      {anterior && (
        <div className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700" data-testid="heredar-en-historial">
          <p>
            La versión {anterior.numero} tenía declarado «{anterior.como}» ({cuandoSubida(anterior.at)}). Si sigue valiendo, no hace falta escribir otra.
          </p>
          <button
            type="button"
            disabled={guardando}
            onClick={() => void correr(() => baseVentasApi.heredarConsentimiento(v.id))}
            className="mt-1 min-h-[44px] rounded-lg border border-violet-200 bg-white px-3 text-xs font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
          >
            Sigue valiendo la de la versión {anterior.numero}
          </button>
        </div>
      )}
      <label htmlFor={idComo} className="block text-xs font-semibold text-slate-600">
        ¿Quién te lo confirmó y cómo?
      </label>
      <input
        id={idComo}
        value={como}
        maxLength={COMO_MAX}
        onChange={(e) => setComo(e.target.value)}
        placeholder="Cecilia, por WhatsApp, 16/10"
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
      />
      <label htmlFor={idRespaldo} className="block text-xs font-semibold text-slate-600">
        Respaldo: la captura o el PDF del mail o del chat donde te lo confirmó (JPG, PNG o PDF, hasta 5 MB)
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputArchivo}
          id={idRespaldo}
          type="file"
          accept={ACEPTA_RESPALDO}
          onChange={(e) => elegirArchivo(e.target.files?.[0] ?? null)}
          className="sr-only"
        />
        <button
          type="button"
          onClick={() => inputArchivo.current?.click()}
          className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
        >
          <Paperclip size={13} aria-hidden="true" /> {archivo ? "Cambiar el respaldo" : "Adjuntar el respaldo"}
        </button>
        {archivo && !errorArchivo && (
          <span className="text-xs text-slate-600" data-testid="respaldo-elegido">
            {archivo.name} · {pesoDelArchivo(archivo.size)}
          </span>
        )}
      </div>
      {errorArchivo && <p role="alert" className="text-sm text-red-700">{errorArchivo}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={!valido || guardando}
          className="min-h-[44px] rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {guardando ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />} Declarar
        </button>
        <button
          type="button"
          disabled={guardando}
          onClick={() => {
            setAbierto(false);
            setError("");
          }}
          className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
